import { Link } from "react-router-dom";
import { Building2, Receipt, AlertCircle } from "lucide-react";
import { useMyProfile, useMyCarteraSummary } from "@/api/portal";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

const CLIENT_TYPE_LABEL: Record<string, string> = {
  PERSONA_NATURAL: "Persona natural",
  PERSONA_JURIDICA: "Persona jurídica",
};

function clientDisplayName(profile?: { businessName: string | null; fullName: string | null; tradeName: string | null }): string {
  if (!profile) return "";
  return profile.businessName || profile.tradeName || profile.fullName || "Cliente";
}

export default function PortalDashboardPage() {
  const { data: profile, isLoading: loadingProfile } = useMyProfile();
  const { data: cartera, isLoading: loadingCartera } = useMyCarteraSummary();

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Bienvenido, {clientDisplayName(profile)}</h1>
        <p className="text-sm text-gray-500">Resumen de su información con la firma.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="card p-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Building2 size={16} className="text-brand-600" /> Datos del cliente
          </div>
          {loadingProfile && <p className="mt-3 text-sm text-gray-400">Cargando...</p>}
          {profile && (
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Tipo</dt>
                <dd className="text-gray-900">{CLIENT_TYPE_LABEL[profile.clientType] ?? profile.clientType}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">{profile.documentType}</dt>
                <dd className="text-gray-900">{profile.documentNumber}</dd>
              </div>
              {profile.email && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Correo</dt>
                  <dd className="text-gray-900">{profile.email}</dd>
                </div>
              )}
              {profile.phone && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Teléfono</dt>
                  <dd className="text-gray-900">{profile.phone}</dd>
                </div>
              )}
              {profile.city && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Ciudad</dt>
                  <dd className="text-gray-900">{profile.city}</dd>
                </div>
              )}
            </dl>
          )}
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Receipt size={16} className="text-brand-600" /> Cartera pendiente
          </div>
          {loadingCartera && <p className="mt-3 text-sm text-gray-400">Cargando...</p>}
          {cartera && (
            <>
              <p className="mt-3 text-2xl font-semibold text-gray-900">{currency.format(cartera.totalOutstanding)}</p>
              {cartera.invoices.length === 0 ? (
                <p className="mt-2 text-sm text-gray-400">No tiene facturas con saldo pendiente.</p>
              ) : (
                <div className="mt-3 space-y-1.5">
                  {cartera.invoices.slice(0, 5).map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">N.° {inv.invoiceNumber}</span>
                      <span className="font-medium text-gray-900">{currency.format(inv.balance)}</span>
                    </div>
                  ))}
                </div>
              )}
              <Link to="/portal/invoices" className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800">
                Ver todas las facturas <AlertCircle size={12} />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
